import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { AUTH_ROUTES, PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/drop-off-location.jpg'),
    alt: 'drop off location page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'drop off location page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Drop-off <span>Storage Locations</span> <span>with ShipAndStorage</span>',
  description:
    'At ShipAndStorage, we make the entire process stress-free with our Drop-off Storage Locations service, available in key cities across the country.',
  points: [
    '6 Months Free Storage',
    'Convenient Drop-Off & Pickup Options',
    'Global Shipping Network',
    'Student-Friendly Solutions'
  ],
  image: {
    url: '/images/v2/pages/main-pages/drop-off-location/hero-image.jpg',
    alt: 'Drop-off Storage Locations with ShipAndStorage'
  },
  ctaButton: { href: AUTH_ROUTES.REGISTER.href, text: 'Drop Off To Storage Now' }
}

export const howToUseDropOffStorageSectionContent = {
  title: 'How to Use Drop-off Storage Locations',
  description: "We've simplified the process, making it easy to ship boxes or store larger items.",

  articles: [
    {
      content: [
        {
          title: 'Choose Your Carrier',
          description: 'Select from our trusted partners, including FedEx, DHL, or UPS for reliable shipping services.'
        },
        {
          title: 'Find a Location',
          description: 'Enter your location on our user-friendly tool to locate the nearest drop-off point.'
        },
        {
          title: 'Drop Off Your Items',
          description: 'Follow the drop-off instructions at your location and leave your items with confidence. '
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/how-to-use-drop-off-storage.svg',
        alt: 'Prepare Your Items feature'
      },
      link: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Store Today' }
    }
  ]
}

export const whyChooseUsSectionContent = {
  title: 'Why Choose Us?',
  description: 'We provide a flexible, secure, and cost-effective solution.',

  cards: [
    {
      id: 1,
      title: 'Wide Coverage',
      description:
        "We've partnered with top logistics companies like FedEx, DHL, and UPS to offer drop-off locations nationwide.",
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/wide-coverage.svg',
        alt: 'Wide Coverage service'
      }
    },
    {
      id: 2,
      title: 'No Hidden Fees',
      description: 'The price you see is the price you pay. There are no surprise fees at the drop-off locations.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/no-hidden-fees.svg',
        alt: 'No Hidden Fees service'
      }
    },
    {
      id: 3,
      title: 'Up to 6 Months of Free Storage',
      description: 'Depending on your plan, you can enjoy up to six months of free storage when using our services.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/up-to-6-months-of-free-storage.svg',
        alt: 'Up to 6 Months of Free Storage service'
      }
    },
    {
      id: 4,
      title: 'Secure & Monitored',
      description:
        'Our locations are secured and monitored to ensure the safety of your items throughout the storage period.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/secure-monitored.svg',
        alt: 'Secure & Monitored service'
      }
    }
  ]
}

export const whatToExpectAtOurDropOffLocationsSectionContent = {
  title: 'What to Expect at Our Drop-off Locations',
  description: 'Drop-off locations offer customer-friendly services for a smooth experience.',

  articles: [
    {
      content: [
        {
          title: 'Free Shipping Materials',
          description:
            'Ask for free label pouches, luggage tags, and cable ties. Show your prepaid shipping label to collect these supplies.'
        },
        {
          title: 'Fast, Convenient Service',
          description:
            'With fast processing at each location, you can drop off your items and be on your way in minutes.'
        },
        {
          title: 'Customer Support',
          description:
            'Our friendly staff is always available to help answer any questions or provide assistance during your drop-off.'
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/what-to-expect-at-our-drop-off.svg',
        alt: 'Prepare Your Items feature'
      }
    }
  ]
}

export const serviceTipsSectionSectionContent = {
  title: 'Pro Tips for Using Drop-off Storage Locations',
  description: 'To ensure your experience goes off without a hitch, keep these tips in mind:',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Start Storing With Us' },

  cards: [
    {
      id: 1,
      title: 'Match the Carrier',
      description:
        'Make sure the carrier (FedEx, DHL, or UPS) aligns with your chosen drop-off location for a seamless experience.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/match-the-carrier.svg',
        alt: 'Match the Carrier service'
      }
    },
    {
      id: 2,
      title: 'Check Cut-off Times',
      description: 'Confirm daily cut-off times to ensure your items are processed the same day.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/check-cut-off-times.svg',
        alt: 'Check Cut-off Times service'
      }
    },
    {
      id: 3,
      title: 'Pack Smartly',
      description:
        'Use sturdy packing materials and securely attach shipping labels to keep items safe and easily identifiable.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/pack-smartly.svg',
        alt: 'Pack Smartly service'
      }
    },
    {
      id: 4,
      title: 'Document Everything',
      description: 'Snap photos of your items with labels attached, and retain a copy of all shipping documents.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/document-everything.svg',
        alt: 'Document Everything service'
      }
    },
    {
      id: 5,
      title: 'Ask for a Receipt',
      description:
        'Always ensure you receive a drop-off receipt as proof of delivery to the storage facility for your records.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/ask-for-a-receipt.svg',
        alt: 'Ask for a Receipt service'
      }
    },
    {
      id: 6,
      title: 'Specific Instructions',
      description:
        'If you have unique handling or timing needs, communicate them clearly to the drop-off center staff.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/specific-instructions.svg',
        alt: 'Specific Instructions service'
      }
    }
  ]
}

export const benefitsSectionSectionContent = {
  title: 'Benefits of Drop-off Storage with ShipAndStorage',

  cards: [
    {
      id: 1,
      title: 'Flexibility',
      description: 'You only pay for what you store. No need to rent large units when you have fewer items to store.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/flexibility.svg',
        alt: 'Flexibility service'
      }
    },
    {
      id: 2,
      title: 'Convenience',
      description:
        "With multiple drop-off locations and global shipping options, it's easy to store and ship items when and where you need.",
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/convenience.svg',
        alt: 'Convenience service'
      }
    },
    {
      id: 3,
      title: 'Cost-effective',
      description: 'Our services are designed to be budget-friendly, making storage accessible for everyone.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/cost-effective.svg',
        alt: 'Cost-effective service'
      }
    },
    {
      id: 4,
      title: 'Secure Storage',
      description:
        'Your items are stored in safe, monitored locations, giving you peace of mind for as long as they’re with us.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/secure-storage.svg',
        alt: 'Secure Storage service'
      }
    }
  ]
}

export const forWhoSectionContent = {
  title: 'Who Benefits from Drop-off Storage?',
  description: 'Our drop-off storage service is perfect for a variety of customers.',

  cards: [
    {
      id: 1,
      title: 'Homeowners',
      description: 'De-clutter your home by storing seasonal items, furniture, and belongings you don’t use every day.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/homeowners.svg',
        alt: 'Homeowners service'
      }
    },
    {
      id: 2,
      title: 'Businesses',
      description:
        'Manage your inventory or store documents, equipment, and supplies without committing to long-term leases.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/businesses.svg',
        alt: 'Businesses service'
      }
    },
    {
      id: 3,
      title: 'Students',
      description:
        'Store your belongings during summer break or study abroad programs, without having to pay for an entire storage unit.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/students.svg',
        alt: 'Students service'
      }
    },
    {
      id: 4,
      title: 'Travelers',
      description:
        'Ship your luggage ahead of time and store it securely until you need it, making your travel stress-free.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/travelers.svg',
        alt: 'Travelers service'
      }
    },
    {
      id: 5,
      title: 'Military Personnel',
      description: 'Store personal items securely during deployments or relocations, with easy access when you return.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/military-personnel.svg',
        alt: 'Military Personnel service'
      }
    },
    {
      id: 6,
      title: 'Event Organizers',
      description: 'Keep event materials, promotional items, and equipment safe and accessible for trade shows.',
      image: {
        src: '/images/v2/pages/main-pages/drop-off-location/event-organizers.svg',
        alt: 'Event Organizers service'
      }
    }
  ]
}

export const faqSectionContent = {
  title: 'Frequently Asked Questions',

  questions: [
    {
      id: 1,
      question: 'Do I need to book in advance to drop off items?',
      answer: 'No, simply find your nearest location and drop off your items when it’s convenient for you.'
    },
    {
      id: 2,
      question: 'What kind of items can I store?',
      answer:
        'You can store a wide variety of items, from boxes and luggage to larger goods like furniture and business inventory.'
    },
    {
      id: 3,
      question: 'Is there a maximum storage period? ',
      answer:
        'No, you can store your items for as long as you need. We also offer up to six months of free storage for select plans.'
    },
    {
      id: 4,
      question: 'What if I need my items back sooner than expected?',
      answer:
        'No problem! You can retrieve your items at any time, just let us know and we’ll arrange for delivery or pickup.'
    }
  ]
}
